import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { ChangeEvent, useEffect, useState } from "react"
import { breakPoints, colors } from "../config/theme"

const Base = styled.div`
  padding: 0 24px;
  min-height: 72px;
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 32px;

  @media only screen and (max-width: ${breakPoints.mobile}) {
    padding: 16px;
  }
`

const Input = styled.input<{ invalid: boolean }>`
  padding: 15px 24px;

  font-family: "Arial";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;

  width: 50%;
  // NOTE magnolia overrides these props with higher specificity thus !important
  height: 48px !important;
  background: ${colors.white} !important;
  border: 1px solid ${colors.greyMid} !important;

  flex: none;
  order: 0;
  flex-grow: 0;

  &::placeholder,
  &:-ms-input-placeholder,
  &::-ms-input-placeholder {
    color: ${colors.greyMid};
  }

  &:focus {
    outline: 2px solid ${colors.greyMid};
  }

  ${({ invalid }) =>
    invalid
      ? css`
          outline: 1px solid ${colors.red} !important;
        `
      : null}

  @media only screen and (max-width: ${breakPoints.tablet}) {
    width: 70%;
  }

  @media only screen and (max-width: ${breakPoints.mobile}) {
    width: 100%;
  }
`

const Error = styled.div`
  font-family: "Arial";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.02em;
  color: ${colors.red};

  width: 50%;

  @media only screen and (max-width: ${breakPoints.tablet}) {
    width: 70%;
  }

  @media only screen and (max-width: ${breakPoints.mobile}) {
    width: 100%;
  }
`

export const NumberAnswer: React.FC<{
  placeholder: string
  value: number | null
  validationError: string
  isValid: boolean
  onChange?: (value: number | null) => void
}> = ({ placeholder, value, onChange, validationError, isValid }) => {
  const [backupValue, setBackupValue] = useState(value?.toString() ?? "")
  const [shouldShowError, setShowError] = useState(false)

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBackupValue(e.target.value)
    const numberValue = parseFloat(e.target.value)
    if (!onChange) return
    onChange(!isNaN(numberValue) ? numberValue : null)
  }

  useEffect(() => {
    setBackupValue(value?.toString() ?? "")
  }, [value])

  useEffect(() => {
    setShowError(
      backupValue !== "" && (!isValid || isNaN(parseFloat(backupValue))),
    )
  }, [backupValue, isValid])

  return (
    <Base>
      <Input
        invalid={shouldShowError}
        type="number"
        placeholder={placeholder}
        value={backupValue}
        onChange={handleInputChange}
      />
      {shouldShowError ? <Error>{validationError}</Error> : null}
    </Base>
  )
}
